import React, { useEffect, useRef, useState } from "react";
import { X, RefreshCw, AlertTriangle, CheckCircle2, Database, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * Portfolio-wide historic-data backfill modal.
 *
 * Kicks off a background job via `POST /monitoring/portfolio/backfill-all-gaps`
 * then polls `GET /monitoring/portfolio/backfill-status` every 2 s to render
 * a live progress bar + per-site rollup + running error list. Refuses to
 * start a second run if one's already in flight (server-side guard).
 */
export default function PortfolioBackfillDialog({ onClose }) {
  const [lookback, setLookback] = useState(365);
  const [fillGapsOnly, setFillGapsOnly] = useState(true);
  const [status, setStatus] = useState(null);   // null = not started
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/monitoring/portfolio/backfill-status");
        setStatus(data);
        if (data.state === "finished" || data.state === "idle") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* transient — keep polling */ }
    }, 2000);
  };

  // On mount, check whether there's already a run in flight — resume the
  // progress modal if so (useful if the operator closed the tab mid-run).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/monitoring/portfolio/backfill-status");
        if (data.state === "running") {
          setStatus(data);
          startPolling();
        }
      } catch { /* silent */ }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/monitoring/portfolio/backfill-all-gaps", {
        lookback_days: lookback, fill_gaps_only: fillGapsOnly,
      });
      if (data.status === "started") {
        toast.success(`Backfill started across ${data.total_sites} site(s)`);
        setStatus({ state: "running", total_sites: data.total_sites,
                    sites_done: 0, sites_ok: 0, sites_error: 0,
                    days_backfilled: 0, total_written: 0, errors: [] });
        startPolling();
      } else if (data.status === "already_running") {
        toast.info("A backfill is already in progress");
        setStatus(data.progress);
        startPolling();
      } else if (data.status === "no_configured_sites") {
        toast.warning("No sites have a configured sync source (MeterOnline / SolarEdge / Solis)");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start backfill");
    } finally { setStarting(false); }
  };

  const pct = status && status.total_sites > 0
    ? Math.min(100, Math.round((status.sites_done / status.total_sites) * 100))
    : 0;
  const isRunning = status?.state === "running";
  const isFinished = status?.state === "finished";

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="portfolio-backfill-dialog">
      <div className="bg-white w-full max-w-2xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label flex items-center gap-2">
              <Database size={11} /> Portfolio-wide historic backfill
            </div>
            <div className="font-display text-xl font-semibold mt-1">
              Fill all data gaps across every site
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"
                  data-testid="portfolio-backfill-close-btn"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          {!isRunning && !isFinished && (
            <>
              <p className="text-sm muted leading-relaxed">
                Iterates every site with a configured sync source (MeterOnline, SolarEdge or Solis) and refetches any day with fewer than 40 half-hourly samples. Safe to re-run — it&apos;s idempotent.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="label mb-1 block">Lookback period</span>
                  <select className="input-swiss w-full font-mono text-xs"
                          value={lookback} onChange={(e) => setLookback(Number(e.target.value))}
                          data-testid="portfolio-lookback-select">
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>1 year</option>
                    <option value={730}>2 years</option>
                    <option value={1095}>3 years (max)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="label mb-1 block">Mode</span>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={fillGapsOnly}
                           onChange={(e) => setFillGapsOnly(e.target.checked)}
                           data-testid="portfolio-fill-gaps-only" />
                    <span>{fillGapsOnly ? "Fill gaps only (idempotent)" : "Force full re-fetch"}</span>
                  </div>
                </label>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 text-[11px] flex items-start gap-2">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>Vendor rate limits (MeterOnline: 40 req pool, 4/min) mean a full-year backfill across 20+ sites can take 10-30 minutes. This runs as a background task — you can close this dialog and re-open to check progress.</span>
              </div>
            </>
          )}

          {(isRunning || isFinished) && status && (
            <>
              <div className="flex items-center justify-between">
                <div className="label">Progress</div>
                <div className="font-mono text-xs">
                  {status.sites_done} / {status.total_sites} sites
                </div>
              </div>
              <div className="w-full h-3 bg-[var(--surface-2)] border border-[var(--line)] overflow-hidden">
                <div className="h-full transition-all"
                     style={{ width: `${pct}%`,
                              background: isFinished
                                ? (status.sites_error > 0 ? "var(--warn)" : "var(--ok)")
                                : "var(--accent)" }}
                     data-testid="portfolio-progress-bar" />
              </div>

              {isRunning && status.current_site && (
                <div className="text-xs muted flex items-center gap-2">
                  <RefreshCw size={12} className="animate-spin" />
                  Processing: <span className="font-mono">{status.current_site.name}</span>
                </div>
              )}

              <div className="grid grid-cols-4 gap-2 mt-2">
                <StatCell label="OK"        value={status.sites_ok}       tone="ok" testid="stat-ok" />
                <StatCell label="Errors"    value={status.sites_error}    tone={status.sites_error > 0 ? "danger" : "muted"} testid="stat-err" />
                <StatCell label="Days"      value={status.days_backfilled} testid="stat-days" />
                <StatCell label="Samples"   value={status.total_written?.toLocaleString?.() || 0} testid="stat-written" />
              </div>

              {status.errors && status.errors.length > 0 && (
                <div className="mt-3 p-3 border border-red-200 bg-red-50" data-testid="portfolio-error-list">
                  <div className="label text-red-800 mb-2">Errors ({status.errors.length})</div>
                  <ul className="text-[10px] font-mono text-red-800 max-h-32 overflow-y-auto space-y-1">
                    {status.errors.slice(-15).map((e, i) => (
                      <li key={i}><strong>{e.project}</strong> — {typeof e.error === "string" ? e.error : JSON.stringify(e.error)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {isFinished && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-center gap-2"
                     data-testid="portfolio-backfill-finished">
                  <CheckCircle2 size={14} />
                  <span className="text-sm">
                    Done. Backfilled <strong>{status.days_backfilled}</strong> day(s) across <strong>{status.sites_ok}</strong> site(s), writing <strong>{(status.total_written || 0).toLocaleString()}</strong> samples.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end items-center gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost"
                  data-testid="portfolio-backfill-cancel-btn">
            {isRunning ? "Close (keeps running)" : "Close"}
          </button>
          {!isRunning && (
            <button onClick={start} disabled={starting}
                    className="btn-primary bg-[var(--accent)] border-[var(--accent)]"
                    data-testid="portfolio-backfill-start-btn">
              <PlayCircle size={13} />
              {starting ? "Starting…" : isFinished ? "Run again" : "Start backfill"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, testid, tone = "muted" }) {
  const tones = {
    ok:     "bg-emerald-50 border-emerald-200 text-emerald-900",
    danger: "bg-red-50 border-red-200 text-red-800",
    muted:  "bg-white border-[var(--line)] text-[var(--ink)]",
  };
  return (
    <div className={`p-3 border ${tones[tone] || tones.muted}`} data-testid={testid}>
      <div className="label">{label}</div>
      <div className="font-display text-lg font-bold mt-0.5 font-mono">{value}</div>
    </div>
  );
}
